// lib.dom has no Trusted Types yet. The URL is typed as a string because register() is, though a browser enforcing them requires the trusted object.
declare global {
  interface Window {
    readonly trustedTypes?: {
      createPolicy(
        name: string,
        rules: { createScriptURL: (url: string) => string },
      ): { createScriptURL: (url: string) => string };
    };
  }
}

export interface ServiceWorkerLog {
  event(name: "infra.sw.registered", fields: Record<string, unknown>): void;
  error(
    name: "infra.sw.error",
    cause: unknown,
    fields: Record<string, unknown>,
  ): void;
}

export interface RegisterServiceWorkerOptions {
  readonly url: string;
  // Production only is the caller's call: under `vite dev` the worker would cache unbundled modules and fight HMR.
  readonly enabled: boolean;
  // Name of the Trusted Types policy the page's CSP allows; the policy admits only `url`.
  readonly trustedTypesPolicy?: string;
  // A factory, evaluated on `load`, so the session id read behind it is the one the logger provider has already minted. Absent means silent.
  readonly log?: () => ServiceWorkerLog;
  // Runs on `load`, before registration.
  readonly onLoad?: (container: ServiceWorkerContainer) => void;
}

export function trustedScriptUrl(url: string, policyName: string): string {
  // oxlint-disable-next-line compat/compat -- optional chaining falls back to the plain URL below the floor
  const policy = window.trustedTypes?.createPolicy(policyName, {
    createScriptURL: (candidate) => {
      if (candidate !== url) {
        throw new Error(`not the service worker: ${candidate}`);
      }
      return url;
    },
  });
  return policy?.createScriptURL(url) ?? url;
}

export function registerServiceWorker(
  options: RegisterServiceWorkerOptions,
): void {
  if (!options.enabled) return;
  if (!("serviceWorker" in navigator)) return;
  const container = navigator.serviceWorker;

  window.addEventListener(
    "load",
    () => {
      options.onLoad?.(container);
      const log = options.log?.();
      const scriptUrl =
        options.trustedTypesPolicy === undefined
          ? options.url
          : trustedScriptUrl(options.url, options.trustedTypesPolicy);
      // "none" keeps the HTTP cache from answering the update check with a stale worker script, so a redeploy (or rollback) is picked up promptly.
      const registering = container.register(scriptUrl, {
        updateViaCache: "none",
      });
      if (log === undefined) {
        void registering;
        return;
      }
      registering
        .then((registration) => {
          log.event("infra.sw.registered", {
            scope: registration.scope,
            // Separates a first-ever install from a revisit that already had a worker running.
            was_active: registration.active !== null,
          });
        })
        .catch((cause: unknown) => {
          log.error("infra.sw.error", cause, { phase: "register" });
        });
    },
    { once: true },
  );
}
